import React, { useState, useEffect } from 'react';
import { Star, Quote, Send, Loader2, MessageSquarePlus, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// External listing on carehome.co.uk
const CAREHOME_URL = "https://www.carehome.co.uk/carehome.cfm/searchazref/20003515ANCA";
// Official carehome.co.uk review score and total count (from listing page, refresh periodically)
const CAREHOME_SCORE = "9.5";
const CAREHOME_REVIEWS_TOTAL = 53;

const relationships = [
  'Son of Resident',
  'Daughter of Resident',
  'Grandson of Resident',
  'Granddaughter of Resident',
  'Brother of Resident',
  'Sister of Resident',
  'Wife of Resident',
  'Husband of Resident',
  'Friend of Resident',
  'Niece of Resident',
  'Nephew of Resident',
  'Other Family Member',
  'Resident',
];

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    rating: 5,
    review_text: '',
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success('Thank you! Your review has been submitted for approval.');
      setFormData({ name: '', relationship: '', rating: 5, review_text: '' });
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '5.0';

  return (
    <section
      id="reviews"
      className="py-20 md:py-32 bg-white"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
            Reviews & Testimonials
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6"
           
          >
            What Families Say
          </h2>
          
          {/* Rating Badge */}
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 px-6 py-3 bg-[#1e3a5f] rounded-full sm:rounded-full text-white mb-4">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#c9a227] fill-current" />
                ))}
              </div>
              <span className="font-bold text-lg">{CAREHOME_SCORE} / 10</span>
            </div>
            <span className="hidden sm:inline text-white/40">•</span>
            <span className="text-white/90 text-sm">{CAREHOME_REVIEWS_TOTAL} reviews on <span className="font-semibold">carehome.co.uk</span></span>
          </div>
          <div className="mb-8">
            <a
              href={CAREHOME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#1e3a5f] hover:text-[#c9a227] font-medium underline-offset-4 hover:underline"
              data-testid="carehome-listing-link"
            >
              Read all {CAREHOME_REVIEWS_TOTAL} reviews on carehome.co.uk
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Write Review Button */}
          <div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full px-6 py-3"
                  data-testid="write-review-btn"
                >
                  <MessageSquarePlus className="w-5 h-5 mr-2" />
                  Write a Review
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-[#1e3a5f]">
                    Share Your Experience
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="review-form">
                  <div>
                    <Label htmlFor="review-name">Your Name *</Label>
                    <Input
                      id="review-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2 rounded-xl"
                      placeholder="Your name"
                      data-testid="review-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="review-relationship">Your Relationship *</Label>
                    <Select
                      value={formData.relationship}
                      onValueChange={(value) => setFormData({ ...formData, relationship: value })}
                    >
                      <SelectTrigger className="mt-2 rounded-xl" data-testid="review-relationship-select">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {relationships.map((rel) => (
                          <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rating *</Label>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className="focus:outline-none"
                          data-testid={`rating-star-${star}`}
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= formData.rating
                                ? 'text-[#c9a227] fill-current'
                                : 'text-slate-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="review-text">Your Review *</Label>
                    <Textarea
                      id="review-text"
                      value={formData.review_text}
                      onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                      required
                      className="mt-2 rounded-xl min-h-[120px]"
                      placeholder="Share your experience with us..."
                      data-testid="review-text-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting || !formData.name || !formData.relationship || !formData.review_text}
                    className="w-full bg-[#1e3a5f] text-white hover:bg-[#162c4a] rounded-full py-3"
                    data-testid="submit-review-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Review
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Your review will be visible after approval by our team.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#c9a227] animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.slice(0, 6).map((review, index) => (
              <div
                key={review.id || index}
                className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#c9a227]/30 hover:shadow-lg transition-all duration-300 flex flex-col"
                data-testid={`testimonial-card-${index}`}
              >
                <Quote className="w-10 h-10 text-[#c9a227]/30 mb-4" />
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < review.rating ? 'text-[#c9a227] fill-current' : 'text-slate-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed line-clamp-5">
                  "{review.review_text}"
                </p>
                <div className="mt-auto">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-medium shrink-0">
                      {review.name?.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-[#1e3a5f] block truncate">{review.name}</span>
                      <span className="text-sm text-slate-500 block truncate">{review.relationship}</span>
                    </div>
                  </div>
                  {/* Source attribution */}
                  {review.source === 'carehome.co.uk' && (
                    <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-500">
                        {review.date ? `${review.date} · ` : ''}
                        <span>via </span>
                        <a
                          href={review.source_url || CAREHOME_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1e3a5f] font-medium hover:text-[#c9a227] inline-flex items-center gap-1"
                        >
                          carehome.co.uk
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <a
            href={CAREHOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-full font-medium transition-colors"
            data-testid="view-all-reviews-btn"
          >
            View all {CAREHOME_REVIEWS_TOTAL} reviews on carehome.co.uk
            <ExternalLink className="w-4 h-4" />
          </a>
          <p className="text-xs text-slate-500 mt-3">
            Reviews displayed here are published with attribution from carehome.co.uk.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
